import React, { useState } from 'react';
import OptionSheet from './OptionSheet';

const SKIN_TONE_OPTIONS = [
  { label: 'Muy clara', color: '#FFDFC4' },
  { label: 'Clara', color: '#F0C8A0' },
  { label: 'Beige', color: '#DEB887' },
  { label: 'Trigueña', color: '#CD853F' },
  { label: 'Oscura', color: '#8B4513' },
  { label: 'Muy oscura', color: '#3C2414' },
];

const HAIR_COLOR_OPTIONS = [
  { label: 'Negro', color: '#232323' },
  { label: 'Castaño oscuro', color: '#4B2E09' },
  { label: 'Castaño claro', color: '#A0522D' },
  { label: 'Rubio', color: '#F8E49B' },
  { label: 'Colorado', color: '#B55239' },
];

export interface ModeloPersonalizadoState {
  genero: string;
  edad: string;
  tipoCuerpo: string;
  tonoPiel: string;
  colorPelo: string;
  largoPelo: string;
}

interface ModeloPersonalizadoOptionProps {
  open: boolean;
  onClose: () => void;
  value: ModeloPersonalizadoState;
  onChange: (v: ModeloPersonalizadoState) => void;
  onAccept: (v: ModeloPersonalizadoState) => void;
}

const ModeloPersonalizadoOption: React.FC<ModeloPersonalizadoOptionProps> = ({
  open,
  onClose,
  value,
  onChange,
  onAccept,
}) => {
  const [local, setLocal] = useState<ModeloPersonalizadoState>(value);

  // Sync local state when opening
  React.useEffect(() => {
    if (open) setLocal(value);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (field: keyof ModeloPersonalizadoState, v: string) => {
    setLocal((prev) => ({ ...prev, [field]: v }));
  };

  const pillBtn = (field: keyof ModeloPersonalizadoState, label: string) => (
    <button
      key={label}
      type="button"
      onClick={() => update(field, local[field] === label ? '' : label)}
      className={`px-4 h-10 rounded-full border text-[15px] ${
        local[field] === label
          ? 'bg-brand-primary text-white border-brand-primary'
          : 'bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50'
      }`}
    >
      {label}
    </button>
  );

  const content = (
    <>
      <p className="text-[15px] text-neutral-500 mb-4">
        Definí las características del modelo para que se ajuste al estilo de tu marca.
      </p>
      <div className="space-y-5">
        {/* Género */}
        <div>
          <label className="block text-[15px] text-neutral-700 mb-2">Género</label>
          <div className="flex gap-2">
            {['Hombre', 'Mujer'].map((g) => pillBtn('genero', g))}
          </div>
        </div>

        {/* Edad */}
        <div>
          <label className="block text-[15px] text-neutral-700 mb-2">Edad</label>
          <div className="flex flex-wrap gap-2">
            {['Bebé', 'Niño', 'Joven (25 años)', 'Adulto/a(30 a 40 años)', 'Mayor'].map((e) =>
              pillBtn('edad', e),
            )}
          </div>
        </div>

        {/* Tipo de cuerpo */}
        <div>
          <label className="block text-[15px] text-neutral-700 mb-2">Tipo de cuerpo</label>
          <div className="flex flex-wrap gap-2">
            {['Promedio', 'Talle Grande', 'Talle Extra Grande'].map((c) =>
              pillBtn('tipoCuerpo', c),
            )}
          </div>
        </div>

        {/* Tono de piel */}
        <div>
          <label className="block text-[15px] text-neutral-700 mb-2">Tono de piel</label>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => update('tonoPiel', '')}
              className={`px-4 h-10 rounded-full border text-[15px] ${
                local.tonoPiel === ''
                  ? 'bg-brand-primary text-white border-brand-primary'
                  : 'bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50'
              }`}
            >
              Cualquiera
            </button>
            {SKIN_TONE_OPTIONS.map(({ label, color }) => (
              <button
                key={label}
                type="button"
                title={label}
                onClick={() => update('tonoPiel', local.tonoPiel === label ? '' : label)}
                className={`w-9 h-9 rounded-full border ${
                  local.tonoPiel === label
                    ? 'ring-2 ring-brand-primary ring-offset-2 border-brand-primary'
                    : 'border-neutral-300'
                } flex items-center justify-center`}
                style={{ backgroundColor: color }}
              >
                {local.tonoPiel === label && (
                  <span className="block w-3.5 h-3.5 rounded-full bg-white border border-brand-primary" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Color de pelo */}
        <div>
          <label className="block text-[15px] text-neutral-700 mb-2">Color de pelo</label>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => update('colorPelo', '')}
              className={`px-4 h-10 rounded-full border text-[15px] ${
                local.colorPelo === ''
                  ? 'bg-brand-primary text-white border-brand-primary'
                  : 'bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50'
              }`}
            >
              Cualquiera
            </button>
            {HAIR_COLOR_OPTIONS.map(({ label, color }) => (
              <button
                key={label}
                type="button"
                title={label}
                onClick={() => update('colorPelo', local.colorPelo === label ? '' : label)}
                className={`w-9 h-9 rounded-full border ${
                  local.colorPelo === label
                    ? 'ring-2 ring-brand-primary ring-offset-2 border-brand-primary'
                    : 'border-neutral-300'
                } flex items-center justify-center`}
                style={{ backgroundColor: color }}
              >
                {local.colorPelo === label && (
                  <span className="block w-3.5 h-3.5 rounded-full bg-white border border-brand-primary" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Largo de pelo */}
        <div>
          <label className="block text-[15px] text-neutral-700 mb-2">Largo de pelo</label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => update('largoPelo', '')}
              className={`px-4 h-10 rounded-full border text-[15px] ${
                local.largoPelo === ''
                  ? 'bg-brand-primary text-white border-brand-primary'
                  : 'bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50'
              }`}
            >
              Cualquiera
            </button>
            {['Rapado', 'Corto', 'Medio', 'Largo'].map((l) => pillBtn('largoPelo', l))}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <OptionSheet
      open={open}
      onClose={() => {
        // Restore original values on cancel
        setLocal(value);
        onClose();
      }}
      onAccept={() => {
        onChange(local);
        onAccept(local);
      }}
      title="Modelo personalizado"
    >
      {content}
    </OptionSheet>
  );
};

export default ModeloPersonalizadoOption;
